import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Bitcoin, Smartphone, Info, Landmark, Banknote, MapPin, Phone } from 'lucide-react';

const COMMON_NETWORKS = [
  { value: 'polygon', label: 'Polygon (MATIC)' },
  { value: 'ethereum', label: 'Ethereum (ETH)' },
  // 💡 Add more networks here as needed:
  // { value: 'arbitrum', label: 'Arbitrum One' },
  // { value: 'optimism', label: 'Optimism' },
  // { value: 'base', label: 'Base' },
  // { value: 'bnb', label: 'BNB Smart Chain (BEP20)' },
  { value: 'other', label: "Other network (type manually)" },
];

export const WithdrawPage = () => {
  const location = useLocation();
  
  // Use FocusedLayout when coming from Super App
  const fromSuperApp = location.state?.fromSuperApp !== false;
  const Layout = fromSuperApp ? FocusedLayout : UserLayout;
  
  const [tab, setTab] = useState('crypto');
  const [wallets, setWallets] = useState([]);
  const [currency, setCurrency] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Phase 4 Withdrawal Engine state
  const [withdrawalLimits, setWithdrawalLimits] = useState(null);
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [calculatingFees, setCalculatingFees] = useState(false);

  // Crypto
  const [blockchainNetworks, setBlockchainNetworks] = useState(COMMON_NETWORKS);
  const [networkChoice, setNetworkChoice] = useState('');
  const [customNetwork, setCustomNetwork] = useState('');
  const [walletAddress, setWalletAddress] = useState('');

  // Mobile Money
  const [momoProviders, setMomoProviders] = useState([]);
  const [momoProviderId, setMomoProviderId] = useState('');
  const [momoDetails, setMomoDetails] = useState({});

  // Bank
  const [bank, setBank] = useState({
    bank_name: '', bank_account_number: '', bank_account_holder: '',
    bank_account_type: '', bank_branch_code: '', bank_swift: '', bank_reference: '',
  });
  
  // Agent-assisted withdrawal
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentNote, setAgentNote] = useState('');

  useEffect(() => {
    userAPI.getWallets().then(res => setWallets(res.data));
    userAPI.browseAgents({ status: 'active', limit: 20 }).then((res) => setAgents(res.data.agents || [])).catch(() => {});
    
    // Fetch blockchain networks
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/blockchain-networks/enabled`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        const networks = data.networks || [];
        // Add "other" option at the end
        setBlockchainNetworks([
          ...networks.map(n => ({
            value: n.network_id,
            label: n.display_name
          })),
          { value: 'other', label: "I'm not sure — let me type it in" }
        ]);
      })
      .catch(() => {
        // Fallback to default networks if API fails
        setBlockchainNetworks(COMMON_NETWORKS);
      });
    
    // Fetch withdrawal limits for Phase 4 crypto withdrawals
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/withdrawals/limits/current`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setWithdrawalLimits(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!currency) { setMomoProviders([]); return; }
    userAPI.getMobileMoneyProviders(currency).then(res => setMomoProviders(res.data)).catch(() => setMomoProviders([]));
    setMomoProviderId('');
    setMomoDetails({});
  }, [currency]);
  
  // Phase 4: Calculate fees when amount or currency changes (crypto only)
  useEffect(() => {
    if (tab === 'crypto' && currency && amount && parseFloat(amount) > 0 && networkChoice && networkChoice !== 'other') {
      const calculateFees = async () => {
        setCalculatingFees(true);
        try {
          const network = networkChoice.toLowerCase();
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/withdrawals/calculate-fees`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              asset_symbol: currency,
              amount: parseFloat(amount),
              network: network
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            setFeeCalculation(data.fees);
          }
        } catch (error) {
          // Silently fail
        } finally {
          setCalculatingFees(false);
        }
      };
      calculateFees();
    } else {
      setFeeCalculation(null);
    }
  }, [currency, amount, networkChoice, tab]);

  const selectedWallet = wallets.find(w => w.currency === currency);
  const selectedMomo = momoProviders.find(p => p.id === momoProviderId);

  const submitCrypto = async (e) => {
    e.preventDefault();
    const network = (networkChoice === 'other' ? customNetwork : networkChoice).trim();
    if (!currency || !network || !walletAddress || !amount) return toast.error('Please fill all fields');
    setLoading(true);
    
    try {
      // Use Phase 4 Withdrawal Engine API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/withdrawals/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset_symbol: currency,
          amount: parseFloat(amount),
          destination_address: walletAddress,
          network: network.toLowerCase(),
          source_type: 'internal_balance'
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Withdrawal request failed');
      }
      
      toast.success('Crypto withdrawal request created successfully!');
      setAmount('');
      setWalletAddress('');
      setFeeCalculation(null);
      userAPI.getWallets().then(res => setWallets(res.data));
    } catch (err) {
      toast.error(err.message || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  const submitMomo = async (e) => {
    e.preventDefault();
    if (!currency || !momoProviderId || !amount) return toast.error('Fill all required fields');
    if (selectedMomo?.required_fields) {
      for (const f of selectedMomo.required_fields) {
        if (f.required && !(momoDetails[f.key] || '').trim()) return toast.error(`${f.label} is required`);
      }
    }
    setLoading(true);
    try {
      await userAPI.withdraw({
        currency, amount: parseFloat(amount),
        method: 'mobile_money',
        mobile_money_provider_id: momoProviderId,
        mobile_money_details: momoDetails,
      });
      toast.success('Mobile money withdrawal submitted for approval');
      setAmount(''); setMomoDetails({});
      userAPI.getWallets().then(res => setWallets(res.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Withdrawal failed');
    } finally { setLoading(false); }
  };

  const submitBank = async (e) => {
    e.preventDefault();
    if (!currency || !amount) return toast.error('Pick a currency and amount');
    if (!bank.bank_name.trim()) return toast.error('Bank name is required');
    if (!bank.bank_account_number.trim()) return toast.error('Account number is required');
    if (!bank.bank_account_holder.trim()) return toast.error('Account holder name is required');
    setLoading(true);
    try {
      await userAPI.withdraw({
        currency, amount: parseFloat(amount),
        method: 'bank',
        ...bank,
      });
      toast.success('Bank withdrawal submitted for approval');
      setAmount('');
      setBank({ bank_name: '', bank_account_number: '', bank_account_holder: '',
                bank_account_type: '', bank_branch_code: '', bank_swift: '', bank_reference: '' });
      userAPI.getWallets().then(res => setWallets(res.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Withdrawal failed');
    } finally { setLoading(false); }
  };

  const submitAgentWithdrawal = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return toast.error('Please select an agent');
    if (!currency) return toast.error('Please select a currency');
    if (!amount || parseFloat(amount) <= 0) return toast.error('Enter a valid amount');
    setLoading(true);
    try {
      await userAPI.requestAgentWithdrawal({
        agent_id: selectedAgent.id,
        currency,
        amount: parseFloat(amount),
        method: 'cash',
        note: agentNote.trim()
      });
      toast.success('Withdrawal request sent! Agent will contact you to arrange cash pickup.');
      setAmount(''); setAgentNote(''); setSelectedAgent(null);
      userAPI.getWallets().then(res => setWallets(res.data));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Withdraw Funds">
      <div className="space-y-6" data-testid="withdraw-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Withdraw Funds</h1>
          <p className="mt-2 text-muted-foreground">Send funds to crypto wallets, mobile money or a bank account</p>
        </div>

        <Card className="max-w-2xl">
          <CardContent className="pt-6">
            <div className="space-y-4 mb-2">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="withdraw-currency-select" className="h-11">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {wallets.map(w => (
                      <SelectItem key={w.currency} value={w.currency}>
                        {w.currency} (Balance: {w.balance.toFixed(2)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number" step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="withdraw-amount-input"
                  className="h-11"
                />
                {selectedWallet && <p className="text-xs text-muted-foreground">Available: {selectedWallet.balance.toFixed(2)} {currency}</p>}
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-6">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
                <TabsTrigger value="crypto" data-testid="tab-crypto"><Bitcoin className="h-4 w-4 mr-1" /> Crypto</TabsTrigger>
                <TabsTrigger value="mobile_money" data-testid="tab-momo"><Smartphone className="h-4 w-4 mr-1" /> Mobile Money</TabsTrigger>
                <TabsTrigger value="bank" data-testid="tab-bank"><Landmark className="h-4 w-4 mr-1" /> Bank</TabsTrigger>
                <TabsTrigger value="agent" data-testid="tab-agent"><Banknote className="h-4 w-4 mr-1" /> Cash via Agent</TabsTrigger>
              </TabsList>

              <TabsContent value="crypto" className="mt-6">
                {/* Phase 4: Withdrawal Limits Info */}
                {withdrawalLimits && withdrawalLimits.available && (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-900 dark:text-blue-300">
                        <strong>Your Limits:</strong> Daily: ${withdrawalLimits.available.daily_available.toFixed(2)} | 
                        Weekly: ${withdrawalLimits.available.weekly_available.toFixed(2)} | 
                        Max per transaction: ${withdrawalLimits.available.per_transaction_max.toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={submitCrypto} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Blockchain / Network</Label>
                    <Select value={networkChoice} onValueChange={setNetworkChoice}>
                      <SelectTrigger data-testid="withdraw-network-select" className="h-11">
                        <SelectValue placeholder="Choose blockchain" />
                      </SelectTrigger>
                      <SelectContent>
                        {blockchainNetworks.map(n => (
                          <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {networkChoice === 'other' && (
                      <Input
                        value={customNetwork}
                        onChange={(e) => setCustomNetwork(e.target.value)}
                        placeholder="Type the network name (e.g. Avalanche, Fantom)"
                        data-testid="withdraw-custom-network-input"
                        className="h-11 mt-2"
                      />
                    )}
                    <p className="text-xs text-muted-foreground flex items-start gap-1 mt-1">
                      <Info className="h-3 w-3 shrink-0 mt-0.5" />
                      Select the blockchain network where your wallet address is located. If your network isn't listed, choose "Other network".
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Wallet Address</Label>
                    <Input
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      placeholder="Recipient wallet address"
                      data-testid="withdraw-address-input"
                      className="h-11 font-mono text-sm"
                    />
                  </div>

                  {/* Phase 4: Fee Breakdown */}
                  {feeCalculation && (
                    <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                      <div className="font-semibold text-sm">Fee Breakdown</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Withdrawal Amount:</span>
                          <span className="font-medium">{feeCalculation.gross_amount} {currency}</span>
                        </div>
                        <div className="flex justify-between text-orange-600 dark:text-orange-400">
                          <span>Network Fee:</span>
                          <span>{feeCalculation.fees.network_fee.toFixed(8)} {currency}</span>
                        </div>
                        <div className="flex justify-between text-orange-600 dark:text-orange-400">
                          <span>Platform Fee ({feeCalculation.fees.platform_fee_percentage}%):</span>
                          <span>{feeCalculation.fees.platform_fee.toFixed(8)} {currency}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between font-bold">
                          <span>You'll Receive:</span>
                          <span className="text-green-600 dark:text-green-400">
                            {feeCalculation.net_amount.toFixed(8)} {currency}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ≈ ${feeCalculation.net_amount_usd.toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      Crypto withdrawals may require cool-down period and admin approval based on amount and account verification level. 
                      Double-check the address and network — crypto sent to the wrong network is unrecoverable.
                    </p>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="withdraw-submit-crypto">
                    {loading ? 'Submitting...' : 'Submit Crypto Withdrawal'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="mobile_money" className="mt-6">
                <form onSubmit={submitMomo} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mobile Money Provider</Label>
                    <Select value={momoProviderId} onValueChange={setMomoProviderId}>
                      <SelectTrigger data-testid="withdraw-momo-provider-select" className="h-11">
                        <SelectValue placeholder={momoProviders.length ? 'Select provider' : currency ? 'No providers for this currency' : 'Pick a currency first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {momoProviders.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}{p.country ? ` — ${p.country}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedMomo && (
                      <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        {selectedMomo.min_amount > 0 && <p>Min: {selectedMomo.min_amount} {selectedMomo.currency || currency}</p>}
                        {selectedMomo.max_amount > 0 && <p>Max per txn: {selectedMomo.max_amount} {selectedMomo.currency || currency}</p>}
                        {selectedMomo.daily_limit > 0 && <p>Daily limit: {selectedMomo.daily_limit} {selectedMomo.currency || currency}</p>}
                        {selectedMomo.description && <p className="mt-1">{selectedMomo.description}</p>}
                      </div>
                    )}
                  </div>

                  {selectedMomo?.required_fields?.map((f) => (
                    <div key={f.key} className="space-y-2">
                      <Label>{f.label}{f.required && <span className="text-red-500">*</span>}</Label>
                      <Input
                        type={f.type || 'text'}
                        value={momoDetails[f.key] || ''}
                        onChange={(e) => setMomoDetails({ ...momoDetails, [f.key]: e.target.value })}
                        placeholder={f.placeholder || f.label}
                        className="h-11"
                        data-testid={`momo-field-${f.key}`}
                      />
                    </div>
                  ))}

                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900">
                    <p className="text-sm text-emerald-800 dark:text-emerald-300">
                      Your withdrawal request will be reviewed by an administrator and paid out via the mobile money provider you selected.
                    </p>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="withdraw-submit-momo">
                    {loading ? 'Submitting...' : 'Submit Mobile Money Withdrawal'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="bank" className="mt-6">
                <form onSubmit={submitBank} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Bank name <span className="text-red-500">*</span></Label>
                    <Input
                      value={bank.bank_name}
                      onChange={(e) => setBank({ ...bank, bank_name: e.target.value })}
                      placeholder="e.g. Standard Bank, FNB, Capitec"
                      className="h-11"
                      data-testid="withdraw-bank-name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Account number <span className="text-red-500">*</span></Label>
                    <Input
                      value={bank.bank_account_number}
                      onChange={(e) => setBank({ ...bank, bank_account_number: e.target.value })}
                      placeholder="e.g. 1234567890"
                      className="h-11 font-mono"
                      data-testid="withdraw-bank-account-number"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Account holder name <span className="text-red-500">*</span></Label>
                    <Input
                      value={bank.bank_account_holder}
                      onChange={(e) => setBank({ ...bank, bank_account_holder: e.target.value })}
                      placeholder="Full name as it appears on the account"
                      className="h-11"
                      data-testid="withdraw-bank-holder"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Account type <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                    <Select value={bank.bank_account_type} onValueChange={(v) => setBank({ ...bank, bank_account_type: v })}>
                      <SelectTrigger className="h-11" data-testid="withdraw-bank-account-type">
                        <SelectValue placeholder="Choose account type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cheque">Cheque / Current</SelectItem>
                        <SelectItem value="savings">Savings</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="transmission">Transmission</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <details className="rounded-lg border border-border p-3">
                    <summary className="text-sm font-medium cursor-pointer">Additional details (optional)</summary>
                    <div className="grid gap-3 md:grid-cols-2 mt-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Branch code</Label>
                        <Input value={bank.bank_branch_code} onChange={(e) => setBank({ ...bank, bank_branch_code: e.target.value })} placeholder="e.g. 250655" data-testid="withdraw-bank-branch" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">SWIFT / BIC (international only)</Label>
                        <Input value={bank.bank_swift} onChange={(e) => setBank({ ...bank, bank_swift: e.target.value.toUpperCase() })} placeholder="e.g. SBZAZAJJ" data-testid="withdraw-bank-swift" />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs">Payment reference</Label>
                        <Input value={bank.bank_reference} onChange={(e) => setBank({ ...bank, bank_reference: e.target.value })} placeholder="What appears on your bank statement" data-testid="withdraw-bank-reference" />
                      </div>
                    </div>
                  </details>

                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Bank withdrawals are reviewed by an administrator and paid manually via EFT. Double-check every field — misspellings can delay or reroute your payout.
                    </p>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="withdraw-submit-bank">
                    {loading ? 'Submitting...' : 'Submit Bank Withdrawal'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="agent" className="space-y-4">
                <form onSubmit={submitAgentWithdrawal} className="space-y-4">
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-emerald-600" />
                      Cash Withdrawal via Agent
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Select an agent, they'll contact you to arrange cash pickup at their location.
                    </p>
                  </div>

                  {/* Agent Selection */}
                  <div>
                    <Label>Select Agent</Label>
                    {selectedAgent ? (
                      <div className="mt-2 p-4 border rounded-lg bg-muted/30">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground">{selectedAgent.business_name || 'Agent'}</h4>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {[selectedAgent.city, selectedAgent.province, selectedAgent.country].filter(Boolean).join(', ')}
                            </p>
                            {selectedAgent.phone && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <Phone className="h-3 w-3" />
                                {selectedAgent.phone}
                              </p>
                            )}
                          </div>
                          <Button type="button" size="sm" variant="outline" onClick={() => setSelectedAgent(null)}>Change</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 max-h-64 overflow-y-auto border rounded-lg divide-y">
                        {agents.length === 0 ? (
                          <p className="p-4 text-center text-sm text-muted-foreground">No agents available. Please try again later.</p>
                        ) : (
                          agents.map((agent) => (
                            <div key={agent.id} className="p-3 hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => setSelectedAgent(agent)}>
                              <h4 className="font-medium text-sm">{agent.business_name || 'Agent'}</h4>
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <MapPin className="h-3 w-3" />
                                {[agent.city, agent.province, agent.country].filter(Boolean).join(', ')}
                              </p>
                              {agent.services && (
                                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                                  {Array.isArray(agent.services) 
                                    ? agent.services.slice(0, 2).join(', ')
                                    : typeof agent.services === 'string'
                                    ? agent.services.split(',').slice(0, 2).join(', ')
                                    : agent.services}
                                </p>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Optional Note */}
                  <div>
                    <Label htmlFor="agent-note-withdraw">Note (Optional)</Label>
                    <Textarea id="agent-note-withdraw" placeholder="Preferred pickup time, location details..." rows={2} value={agentNote} onChange={(e) => setAgentNote(e.target.value)} />
                  </div>

                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Next Steps:</strong> After submitting, the agent will contact you to arrange cash pickup. Visit their location, show your ID, and receive cash.
                    </p>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading || !selectedAgent}>
                    {loading ? 'Submitting...' : 'Submit Withdrawal Request'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
